import { z } from 'zod';

// ---------------------------------------------------------------------------
// Period format regex (YYYY-MM)
// ---------------------------------------------------------------------------

const periodoRegex = /^\d{4}-(0[1-9]|1[0-2])$/;

// ---------------------------------------------------------------------------
// Closure status enum
// ---------------------------------------------------------------------------

export const ClosureStatusSchema = z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED']);
export type ClosureStatus = z.infer<typeof ClosureStatusSchema>;

// ---------------------------------------------------------------------------
// Reprogramacion status enum
// ---------------------------------------------------------------------------

export const ReprogramacionStatusSchema = z.enum(['PENDING', 'IN_PROGRESS', 'COMPLETED', 'FAILED']);
export type ReprogramacionStatus = z.infer<typeof ReprogramacionStatusSchema>;

// ---------------------------------------------------------------------------
// Readiness query (GET /closure/readiness)
// ---------------------------------------------------------------------------

export const ClosureReadinessQuerySchema = z.object({
  ambitoId: z.coerce.number().int().positive(),
  periodo: z.string().regex(periodoRegex, 'Formato: YYYY-MM'),
});
export type ClosureReadinessQuery = z.infer<typeof ClosureReadinessQuerySchema>;

// ---------------------------------------------------------------------------
// Readiness response
// ---------------------------------------------------------------------------

export const ClosureReadinessSchema = z.object({
  ambitoId: z.number(),
  periodo: z.string(),
  counts: z.record(z.number()),
  totalTickets: z.number(),
  approvedCount: z.number(),
  signedPdfCount: z.number(),
  unsignedPdfCount: z.number(),
  readyToClose: z.boolean(),
  blockers: z.array(z.string()),
});
export type ClosureReadiness = z.infer<typeof ClosureReadinessSchema>;

// ---------------------------------------------------------------------------
// Execute closure request (POST /closure/execute)
// ---------------------------------------------------------------------------

export const ExecuteClosureSchema = z.object({
  ambitoId: z.number().int().positive(),
  periodo: z.string().regex(periodoRegex, 'Formato: YYYY-MM'),
  parentClosureId: z.number().int().positive().optional(),
  addendumReason: z.string().max(500).optional(),
  // Re-generate the package even if a COMPLETED closure already exists for this
  // ambito+periodo (re-arms the ZIP + re-sends the email to the initiator).
  regenerate: z.boolean().optional(),
});
export type ExecuteClosureInput = z.infer<typeof ExecuteClosureSchema>;

// ---------------------------------------------------------------------------
// Execute closure response
// ---------------------------------------------------------------------------

export const ExecuteClosureResultSchema = z.object({
  closureLogId: z.number(),
  status: ClosureStatusSchema,
  ticketCount: z.number(),
  approvedCount: z.number(),
  rejectedCount: z.number(),
});
export type ExecuteClosureResult = z.infer<typeof ExecuteClosureResultSchema>;

// ---------------------------------------------------------------------------
// Closure log response (single entry)
// ---------------------------------------------------------------------------

export const ClosureLogResponseSchema = z.object({
  id: z.number(),
  ambitoId: z.number(),
  periodo: z.string(),
  status: ClosureStatusSchema,
  ticketCount: z.number(),
  approvedCount: z.number(),
  rejectedCount: z.number(),
  errorCount: z.number(),
  isAddendum: z.boolean(),
  parentClosureId: z.number().nullable(),
  addendumReason: z.string().nullable(),
  packageS3Key: z.string().nullable(),
  packageGeneratedAt: z.string().nullable(),
  emailSentAt: z.string().nullable(),
  emailError: z.string().nullable(),
  startedAt: z.string(),
  completedAt: z.string().nullable(),
  createdBy: z.number(),
});
export type ClosureLogResponse = z.infer<typeof ClosureLogResponseSchema>;

// ---------------------------------------------------------------------------
// Closure list filter (GET /closure/history)
// ---------------------------------------------------------------------------

export const ClosureListFilterSchema = z.object({
  ambitoId: z.coerce.number().int().positive().optional(),
  periodo: z.string().regex(periodoRegex, 'Formato: YYYY-MM').optional(),
  status: ClosureStatusSchema.optional(),
});
export type ClosureListFilter = z.infer<typeof ClosureListFilterSchema>;

// ---------------------------------------------------------------------------
// Reprogramacion trigger request (POST /closure/reprogramacion/trigger)
// ---------------------------------------------------------------------------

export const ReprogramacionTriggerSchema = z.object({
  ambitoId: z.number().int().positive().optional(),
  periodo: z.string().regex(periodoRegex, 'Formato: YYYY-MM').optional(),
});
export type ReprogramacionTriggerInput = z.infer<typeof ReprogramacionTriggerSchema>;

// ---------------------------------------------------------------------------
// Reprogramacion result
// ---------------------------------------------------------------------------

export const ReprogramacionResultSchema = z.object({
  scanned: z.number(),
  reprogrammed: z.number(),
  skipped: z.number(),
  failed: z.number(),
  errors: z.array(z.string()),
  logId: z.number().optional(),
});
export type ReprogramacionResult = z.infer<typeof ReprogramacionResultSchema>;
